import { useCallback, useEffect, useRef } from 'react';

type ShortcutTarget = Document | HTMLElement | null;

type ShortcutTargetRef = {
  current: ShortcutTarget;
};

export interface ShortcutConfig {
  key: string;
  chord?: string;
  action: (event: KeyboardEvent) => void;
  description: string;
  category: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
  shiftKey?: boolean;
  preventDefault?: boolean;
  stopPropagation?: boolean;
  allowInInputs?: boolean;
}

interface KeyboardShortcutEngineOptions {
  enabled?: boolean;
  target?: ShortcutTarget | ShortcutTargetRef;
}

function normalizeKey(key: string): string {
  return key.length === 1 ? key.toLowerCase() : key.toLowerCase();
}

function matchesModifier(expected: boolean | undefined, actual: boolean): boolean {
  return expected === undefined ? !actual : expected === actual;
}

function resolveTarget(target?: ShortcutTarget | ShortcutTargetRef): ShortcutTarget {
  if (!target) return typeof document !== 'undefined' ? document : null;
  if (typeof target === 'object' && target !== null && 'current' in target) return target.current;
  return target;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tagName = target.tagName;
  return tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT' || target.isContentEditable;
}

function matchesShortcut(shortcut: ShortcutConfig, event: KeyboardEvent, key: string): boolean {
  return (
    normalizeKey(shortcut.key) === key &&
    matchesModifier(shortcut.ctrlKey, event.ctrlKey) &&
    matchesModifier(shortcut.metaKey, event.metaKey) &&
    matchesModifier(shortcut.altKey, event.altKey) &&
    matchesModifier(shortcut.shiftKey, event.shiftKey)
  );
}

export function useKeyboardShortcutEngine(shortcuts: ShortcutConfig[], options: KeyboardShortcutEngineOptions = {}): void {
  const { enabled = true, target } = options;
  const chordRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearChord = useCallback(() => {
    chordRef.current = null;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    const key = normalizeKey(event.key);
    const typing = isTypingTarget(event.target);

    const applyMatch = (match: ShortcutConfig | undefined) => {
      if (!match) return false;
      if (typing && !match.allowInInputs) return false;
      if (match.preventDefault !== false) event.preventDefault();
      if (match.stopPropagation) event.stopPropagation();
      match.action(event);
      return true;
    };

    if (chordRef.current) {
      const activeChord = chordRef.current;
      clearChord();

      const chordMatch = shortcuts.find((shortcut) => (
        shortcut.chord
        && normalizeKey(shortcut.chord) === activeChord
        && matchesShortcut(shortcut, event, key)
      ));

      if (applyMatch(chordMatch)) return;
    }

    const chordStarter = shortcuts.find((shortcut) => {
      if (!shortcut.chord) return false;
      if (typing && !shortcut.allowInInputs) return false;
      return normalizeKey(shortcut.chord) === key
        && matchesModifier(shortcut.ctrlKey, event.ctrlKey)
        && matchesModifier(shortcut.metaKey, event.metaKey)
        && matchesModifier(shortcut.altKey, event.altKey)
        && matchesModifier(shortcut.shiftKey, event.shiftKey);
    });

    if (chordStarter) {
      chordRef.current = key;
      timerRef.current = setTimeout(() => {
        chordRef.current = null;
        timerRef.current = null;
      }, 500);
      return;
    }

    const match = shortcuts.find((shortcut) => !shortcut.chord && matchesShortcut(shortcut, event, key));
    applyMatch(match);
  }, [clearChord, enabled, shortcuts]);

  useEffect(() => {
    const shortcutTarget = resolveTarget(target);
    if (!shortcutTarget) return;
    const listener: EventListener = (event) => handleKeyDown(event as KeyboardEvent);

    shortcutTarget.addEventListener('keydown', listener);
    return () => {
      shortcutTarget.removeEventListener('keydown', listener);
      clearChord();
    };
  }, [clearChord, handleKeyDown, target]);
}

export function useKeyboardShortcuts(shortcuts: ShortcutConfig[], enabled = true): void {
  useKeyboardShortcutEngine(shortcuts, { enabled });
}
